/**
 * Wishlist Page Component
 * Displays all wishlist products with infinite scroll pagination
 *
 * Uses WishlistState for product handles when in-house wishlist is enabled,
 * falls back to Rivo + localStorage for backwards compatibility.
 */

const LIKED_STORAGE_KEY = 'theme:liked-products';
const AUTO_LOAD_LIMIT = 3; // Auto-load 3 pages, then show "Load More" button

/**
 * Get the locale-aware search URL
 */
function getSearchUrl() {
  if (window.theme?.routes?.search_url) {
    return window.theme.routes.search_url;
  }

  const pathname = window.location.pathname;
  const localeMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);

  if (localeMatch) {
    return `/${localeMatch[1]}/search`;
  }

  return '/search';
}

class WishlistPage extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.currentPage = -1; // Start at -1 so first increment brings us to 0
    this.allHandles = [];
    this.productsPerPage = 16;
    this.autoLoadedPagesCount = 0;
    this.shouldObserveNext = true;
    this.infinityScrollObserver = null;
    this.useInhouseWishlist = false;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.productsPerPage = parseInt(this.getAttribute('data-per-page')) || 16;
    this.sectionId = this.getAttribute('data-section-id');
    this.useInhouseWishlist = this.hasAttribute('data-inhouse-wishlist');

    // Get elements
    this.grid = this.querySelector('.wishlist-page__grid');
    this.viewMoreTrigger = this.querySelector('.wishlist-page__view-more-trigger');
    this.loadMoreWrapper = this.querySelector('.load-more-wrapper');
    this.loadMoreButton = this.loadMoreWrapper?.querySelector('.load-more-button');
    this.emptyState = this.querySelector('.wishlist-page__empty');

    // Initialize
    this.init();
  }

  disconnectedCallback() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  /**
   * Initialize wishlist page
   */
  async init() {
    // Load all handles based on system type
    await this.loadAllHandles();

    if (this.allHandles.length === 0) {
      this.showEmptyState();
      return;
    }

    // Load first page
    this.renderNextPage();

    // Setup infinite scroll observer
    this.#observeViewMore();

    // Subscribe to wishlist changes for live updates
    if (this.useInhouseWishlist) {
      this.#subscribeToWishlistChanges();
    }
  }

  /**
   * Subscribe to WishlistState changes for live updates
   */
  #subscribeToWishlistChanges() {
    // Wait for WishlistState to be available
    const maxAttempts = 20;
    let attempts = 0;

    const check = () => {
      if (window.WishlistState) {
        this.unsubscribe = window.WishlistState.subscribe((handles) => {
          this.#handleWishlistChange(handles);
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, 100);
      }
    };

    check();
  }

  /**
   * Handle wishlist state changes (item removed).
   * Only handles removals — additions aren't possible from the wishlist page
   * since there's no "add" UI here. If server sync adds items after init,
   * they'll appear on next page load.
   */
  #handleWishlistChange(newHandles) {
    const newHandleSet = new Set(newHandles);

    // Find removed handles
    const removedHandles = this.allHandles.filter(h => !newHandleSet.has(h));

    if (removedHandles.length > 0) {
      // Remove cards for removed products
      removedHandles.forEach(handle => {
        const card = this.grid?.querySelector(`[data-product-handle="${handle}"]`);
        if (card) {
          const item = card.closest('.resource-list__item');
          if (item) {
            item.classList.add('is-removing');
            setTimeout(() => item.remove(), 300);
          }
        }
      });

      // Update handles list
      this.allHandles = newHandles;

      // Show empty state if no items left
      if (this.allHandles.length === 0) {
        setTimeout(() => this.showEmptyState(), 350);
      }
    }
  }

  /**
   * Load all handles based on system type
   */
  async loadAllHandles() {
    if (this.useInhouseWishlist) {
      // Use WishlistState (in-house system)
      await this.#loadFromWishlistState();
    } else {
      // Legacy: use Rivo + localStorage
      await this.#loadFromLegacySources();
    }
  }

  /**
   * Load handles from WishlistState (in-house system)
   */
  async #loadFromWishlistState() {
    // Wait for WishlistState to be available
    const maxAttempts = 20;
    let attempts = 0;

    return new Promise((resolve) => {
      const check = () => {
        if (window.WishlistState) {
          // Reverse so newest-liked appears first (server stores oldest→newest)
          this.allHandles = window.WishlistState.getLikedHandles().slice().reverse();
          console.log(`[WishlistPage] Loaded ${this.allHandles.length} items from WishlistState (newest first)`);
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, 100);
        } else {
          // Fallback to localStorage directly, also reversed for newest-first
          this.allHandles = this.getLocalStorageHandles().slice().reverse();
          console.log(`[WishlistPage] WishlistState not available, loaded ${this.allHandles.length} items from localStorage (newest first)`);
          resolve();
        }
      };

      check();
    });
  }

  /**
   * Load handles from legacy sources (Rivo + localStorage)
   */
  async #loadFromLegacySources() {
    if (this.isLoggedIn()) {
      // Get from BOTH sources
      const rivoProductIds = await this.getRivoProductIds();
      const rivoHandles = await this.convertIdsToHandles(rivoProductIds);
      const localHandles = this.getLocalStorageHandles();

      // Merge and deduplicate (Rivo takes precedence for ordering)
      const seenHandles = new Set(rivoHandles);
      const uniqueLocalHandles = localHandles.filter(handle => {
        if (seenHandles.has(handle)) {
          return false;
        }
        seenHandles.add(handle);
        return true;
      });

      // Rivo products first, then localStorage-only products
      this.allHandles = [...rivoHandles, ...uniqueLocalHandles];

      console.log(`[WishlistPage] Merged wishlists: ${rivoHandles.length} from Rivo, ${uniqueLocalHandles.length} from localStorage only`);
    } else {
      // Not logged in: use localStorage only
      this.allHandles = this.getLocalStorageHandles();
    }
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    // Theme is defined as a const in scripts.liquid, not window.Theme
    if (typeof Theme !== 'undefined' && Theme.customer) {
      return Theme.customer.loggedIn === true;
    }
    return false;
  }

  /**
   * Get wishlist product handles from localStorage
   */
  getLocalStorageHandles() {
    try {
      const stored = localStorage.getItem(LIKED_STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[WishlistPage] Error reading liked products from localStorage:', error);
      return [];
    }
  }

  /**
   * Get wishlist product IDs from Rivo (legacy)
   */
  async getRivoProductIds() {
    try {
      // Check if RivoJS is available
      if (typeof RivoJS === 'undefined' || !RivoJS.getFavoriteProducts) {
        console.warn('[WishlistPage] RivoJS not available');
        return [];
      }

      // Get favorite products from Rivo
      const resp = await RivoJS.getFavoriteProducts();

      // Check if the response is successful
      if (!resp || !resp.success || !resp.products || resp.products.length === 0) {
        return [];
      }

      // Extract product IDs from Rivo response
      const productIds = resp.products
        .map(p => p.remote_id)
        .filter(id => id);

      return productIds;
    } catch (error) {
      console.error('[WishlistPage] Error fetching from Rivo:', error);
      return [];
    }
  }

  /**
   * Convert product IDs to handles (legacy)
   */
  async convertIdsToHandles(productIds) {
    if (!Array.isArray(productIds) || productIds.length === 0) {
      return [];
    }

    // Fetch all products in parallel
    const fetchPromises = productIds.map(async (id) => {
      try {
        // Use Shopify admin API endpoint to get product by ID
        const productUrl = `/products.json?ids=${id}`;
        const response = await fetch(productUrl);

        if (!response.ok) {
          console.warn(`[WishlistPage] Product ID ${id} not found`);
          return null;
        }

        const data = await response.json();
        if (data.products && data.products[0] && data.products[0].handle) {
          return data.products[0].handle;
        }

        return null;
      } catch (error) {
        console.error(`[WishlistPage] Error fetching product ID ${id}:`, error);
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);
    return results.filter(handle => handle !== null);
  }

  /**
   * Calculate total pages
   */
  getTotalPages() {
    return Math.ceil(this.allHandles.length / this.productsPerPage);
  }

  /**
   * Get handles for a specific page (0-indexed)
   */
  getPageHandles(page) {
    const startIndex = page * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    return this.allHandles.slice(startIndex, endIndex);
  }

  /**
   * Check if there are more pages to load
   */
  hasNextPage() {
    return this.currentPage < this.getTotalPages() - 1;
  }

  /**
   * Setup IntersectionObserver for infinite scroll
   */
  #observeViewMore() {
    if (!this.viewMoreTrigger) return;

    // Create observer
    this.infinityScrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && this.shouldObserveNext) {
            this.renderNextPage();
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    // Observe the trigger
    if (this.shouldObserveNext) {
      this.infinityScrollObserver.observe(this.viewMoreTrigger);
    }
  }

  /**
   * Render the next page of products
   */
  async renderNextPage() {
    if (this.loading || !this.hasNextPage()) {
      return;
    }

    this.loading = true;
    this.currentPage++;

    try {
      // Get handles for this page
      const pageHandles = this.getPageHandles(this.currentPage);

      if (pageHandles.length === 0) {
        return;
      }

      // Only show skeleton loader for pages after the first
      // (First page uses server-rendered skeletons)
      if (this.currentPage > 0) {
        this.#showSkeletonLoader();
      }

      // Fetch and render products
      await this.#fetchAndAppendProducts(pageHandles);

      // Hide skeleton loader (only affects JS-added skeletons)
      this.#hideSkeletonLoader();

      // Increment auto-load counter
      this.autoLoadedPagesCount++;

      // Check if we should show "Load More" button
      if (this.autoLoadedPagesCount > 0 && this.autoLoadedPagesCount % AUTO_LOAD_LIMIT === 0) {
        // Stop auto-loading
        this.shouldObserveNext = false;

        // Unobserve trigger
        if (this.viewMoreTrigger && this.infinityScrollObserver) {
          this.infinityScrollObserver.unobserve(this.viewMoreTrigger);
        }

        // Show Load More button
        this.#showLoadMoreButton();
      }

    } catch (error) {
      console.error('[WishlistPage] Error rendering next page:', error);
      this.#hideSkeletonLoader();
    } finally {
      this.loading = false;
    }
  }

  /**
   * Fetch products and append to grid
   */
  async #fetchAndAppendProducts(handles) {
    try {
      // Build search query
      const searchQuery = handles.map(handle => `handle:${handle}`).join(' OR ');

      // Fetch rendered section using search endpoint
      const searchUrl = getSearchUrl();
      const url = `${searchUrl}?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${encodeURIComponent(this.sectionId)}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch section HTML');
      }

      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid
      const productGrid = doc.querySelector('.wishlist-page__grid');

      if (!productGrid) {
        throw new Error('Product grid not found in response');
      }

      // Get product cards and sort them
      const items = Array.from(productGrid.querySelectorAll('.resource-list__item'));
      const sortedItems = this.#sortProductCards(items, handles);

      if (!this.grid || sortedItems.length === 0) {
        return;
      }

      // On first page, replace server-rendered skeletons
      if (this.currentPage === 0) {
        this.grid.innerHTML = '';
      }

      // Append to grid
      this.grid.append(...sortedItems);

      // Reinitialize wishlist buttons based on system type
      requestAnimationFrame(() => {
        if (this.useInhouseWishlist) {
          // In-house wishlist buttons auto-initialize via custom element
        } else {
          this.#reinitializeRivoButtons();
        }
      });

    } catch (error) {
      console.error('[WishlistPage] Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Sort product cards to match handle order
   */
  #sortProductCards(items, handles) {
    // Create map of handle -> element
    const cardMap = new Map();
    items.forEach(item => {
      const link = item.querySelector('a[href*="/products/"]');
      if (link) {
        const href = link.getAttribute('href');
        const match = href.match(/\/products\/([^/?]+)/);
        if (match) {
          cardMap.set(match[1], item);

          // Add data attribute for live update tracking
          const wishlistBtn = item.querySelector('wishlist-button');
          if (wishlistBtn) {
            wishlistBtn.setAttribute('data-product-handle', match[1]);
          }
        }
      }
    });

    // Build sorted array
    const sortedItems = [];
    for (const handle of handles) {
      const item = cardMap.get(handle);
      if (item) {
        sortedItems.push(item);
      }
    }

    return sortedItems;
  }

  /**
   * Show skeleton loading indicator
   */
  #showSkeletonLoader() {
    if (!this.grid) return;

    const skeletonCards = Array.from({ length: this.productsPerPage }, () => {
      const skeletonItem = document.createElement('div');
      skeletonItem.className = 'resource-list__item';
      skeletonItem.setAttribute('data-skeleton-item', 'true');
      skeletonItem.innerHTML = `
        <div class="product-card-skeleton">
          <div class="product-card-skeleton__image"></div>
          <div class="product-card-skeleton__content">
            <div class="product-card-skeleton__title"></div>
            <div class="product-card-skeleton__price"></div>
          </div>
        </div>
      `;
      return skeletonItem;
    });

    skeletonCards.forEach(card => this.grid.appendChild(card));
  }

  /**
   * Hide skeleton loading indicator
   */
  #hideSkeletonLoader() {
    const skeletonItems = this.querySelectorAll('[data-skeleton-item="true"]');
    skeletonItems.forEach(item => {
      item.classList.add('is-removing');
      setTimeout(() => item.remove(), 300);
    });
  }

  /**
   * Show the Load More button
   */
  #showLoadMoreButton() {
    if (!this.loadMoreWrapper || !this.loadMoreButton) return;

    // Check if there are more pages
    if (!this.hasNextPage()) {
      this.loadMoreWrapper.style.display = 'none';
      return;
    }

    // Show button
    this.loadMoreWrapper.style.display = '';
    this.loadMoreButton.disabled = false;

    // Setup click handler
    const handleClick = async () => {
      // Disable button
      this.loadMoreButton.disabled = true;

      // Hide button
      this.loadMoreWrapper.style.display = 'none';

      // Load next page
      await this.renderNextPage();

      // Reset counter to start new auto-load cycle
      this.autoLoadedPagesCount = 0;

      // Re-enable auto-loading
      this.shouldObserveNext = true;

      // Re-observe trigger
      if (this.viewMoreTrigger && this.infinityScrollObserver) {
        this.infinityScrollObserver.observe(this.viewMoreTrigger);
      }

      // Remove click handler
      this.loadMoreButton.removeEventListener('click', handleClick);
    };

    // Add click handler
    this.loadMoreButton.addEventListener('click', handleClick);
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    if (this.grid) {
      this.grid.style.display = 'none';
    }
    if (this.loadMoreWrapper) {
      this.loadMoreWrapper.style.display = 'none';
    }
    if (this.emptyState) {
      this.emptyState.style.display = 'block';
    }
  }

  /**
   * Reinitialize Rivo favorite buttons (legacy)
   */
  #reinitializeRivoButtons() {
    try {
      if (window.RivoProfileAlpine) {
        const rivoAccountWidget = document.getElementById('rivo-account-slideout');
        if (rivoAccountWidget) {
          window.RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
        }
      }
    } catch (error) {
      console.error('[WishlistPage] Error reinitializing Rivo buttons:', error);
    }
  }
}

// Register the custom element
if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
